/**
 * Wiederverwendbare öffentliche SEO-Einstiegsseite — `/{sprache}/{slug}`.
 *
 * Jede Seite steht für EINE Suchabsicht (z. B. „Wohnung mieten Wien“)
 * und liefert sofort eine brauchbare Ergebnisansicht: vorbelegte Filter,
 * echte Inserate, Suchalarm, verwandte Einstiege. Keine Anmeldung, keine
 * stille geografische Ausweitung, keine erfundene Trefferzahl.
 *
 * Definition kommt aus `src/seo/landing-pages.json` (Spiegel der
 * Backend-Registry) — die Metadaten stehen damit ohne API-Aufruf fest.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { getPublicFeed } from "../api/publicApi";
import DarkSelect from "../components/home/DarkSelect";
import PublicDealCard from "../components/public/PublicDealCard";
import SeoAlertButton from "../components/seo/SeoAlertButton";
import SeoLinkList from "../components/seo/SeoLinkList";
import NotFoundPage from "./NotFoundPage";
import { useAuth } from "../auth/AuthContext";
import { trackFunnel } from "../lib/funnel";
import { paramsToQuery } from "../lib/searchQuery";
import {
  SEO_HOST, SEO_LANGS, SEO_STATE_THIN, SEO_X_DEFAULT, findSeoPage,
  relatedSeoPages, seoAlternates, seoFeedParams, seoIndexState,
  seoMinResults, seoPath, seoRobots,
} from "../seo/landing";
import useSeoHead from "../seo/useSeoHead";

const OG_LOCALE = { de: "de_AT", en: "en_GB", hr: "hr_HR" };

export default function SeoLandingPage() {
  const { lang, slug } = useParams();
  const page = findSeoPage(lang, slug);
  const { t, i18n } = useTranslation(["seo", "enums", "discover"]);
  const { user } = useAuth();
  const navigate = useNavigate();

  const ambiguous = (page?.ambiguous || []).includes("transaction_type");
  const [tt, setTt] = useState(page?.filters?.transaction_type || "");
  const [dealsOnly, setDealsOnly] = useState(page?.filters?.quality === "deals");
  const [sort, setSort] = useState(
    page?.filters?.quality === "deals" ? "recommended" : "newest");

  // Die URL ist die explizite Sprachwahl des Nutzers — auch dann, wenn
  // der Slug unbekannt ist: sonst erscheint die 404-Ansicht in der
  // zuletzt benutzten Sprache statt in der der URL.
  useEffect(() => {
    if (lang && SEO_LANGS.includes(lang)
        && i18n.language?.slice(0, 2) !== lang) {
      i18n.changeLanguage(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, page?.key]);

  useEffect(() => {
    if (page) trackFunnel("seo_landing_view", { key: page.key, lang });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.key, lang]);

  const feedParams = useMemo(() => {
    if (!page) return null;
    return seoFeedParams(page, {
      limit: 24,
      sort,
      quality: dealsOnly ? "deals" : "all",
      transaction_type: tt || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.key, sort, dealsOnly, tt]);

  const feedQ = useInfiniteQuery({
    queryKey: ["seo-landing", page?.key, feedParams],
    enabled: !!page,
    queryFn: async ({ pageParam }) =>
      getPublicFeed(page.vertical, { ...feedParams, ...(pageParam || {}) }),
    initialPageParam: null,
    getNextPageParam: (last) => {
      if (last?.next_cursor) return { cursor: last.next_cursor };
      if (last?.next_page) return { page: last.next_page };
      return undefined;
    },
    staleTime: 60_000,
  });

  const items = (feedQ.data?.pages || []).flatMap((p) => p.items || []);
  const meta = feedQ.data?.pages?.[0]?.meta;
  const total = meta?.total_matches ?? null;
  // `null` heisst NICHT „0 Treffer": der Ladevorgang laeuft noch, die
  // Anfrage ist fehlgeschlagen oder der Zaehler war nicht verfuegbar
  // (`meta.total_unavailable`). Die gemeinsame Regel entscheidet daraus
  // genau dasselbe wie das Build-Skript im ausgelieferten HTML.
  const knownCount = (!meta || meta.total_unavailable || feedQ.isError)
    ? null : total;
  const indexState = seoIndexState(knownCount, seoMinResults(page || {}));
  const thin = indexState === SEO_STATE_THIN;

  const loc = page?.i18n?.[lang];
  const canonical = page ? `${SEO_HOST}${seoPath(lang, page)}` : "";
  const alternates = page ? seoAlternates(page) : [];
  const xDefault = page
    ? (alternates.find((a) => a.lang === SEO_X_DEFAULT) || alternates[0])?.href
    : undefined;

  useSeoHead({
    // Unbekannter Slug → die 404-Ansicht verwaltet den Kopf selbst
    // (`noindex`). Ohne diesen Schalter würde der Kopf dieser Seite das
    // `noindex` wieder auf `index` zurückdrehen.
    enabled: !!(page && loc),
    lang: lang || "de",
    title: loc?.title || "",
    description: loc?.description || "",
    canonical,
    alternates,
    xDefault,
    // Ehrlich: eine nachweislich zu dünne Seite gehört nicht in den
    // Index. Unbekannt bleibt indexierbar — siehe `seo/indexability.js`.
    robots: seoRobots(indexState),
    ogLocale: OG_LOCALE[lang],
    ogImage: `${SEO_HOST}/og-dealalert.png`,
    jsonLd: page ? [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "DealAlert",
        url: `${SEO_HOST}/`,
        logo: `${SEO_HOST}/logo-512.png`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1,
            name: t("seo:breadcrumb.home"), item: `${SEO_HOST}/` },
          { "@type": "ListItem", position: 2, name: loc?.h1, item: canonical },
        ],
      },
    ] : [],
  });

  if (!page || !loc) return <NotFoundPage />;

  const openCard = (card) => {
    trackFunnel("deal_detail_open", { ulid: card.id, authed: !!user });
    navigate(user ? `/listing/${card.id}` : `/deal/${card.id}`);
  };
  const openFullSearch = () => {
    const q = paramsToQuery({ ...page.filters, transaction_type: tt || null,
                              quality: dealsOnly ? "deals" : null, sort });
    navigate(`/search/${page.vertical}?${q}`);
  };

  const chips = Object.entries(page.filters)
    .filter(([k]) => k !== "quality")
    .map(([k, v]) => {
      if (k === "transaction_type") return t(`enums:transaction_type.${v}`);
      if (k === "object_type") return t(`enums:object_type.${v}`);
      if (k === "yield_min") return `≥ ${v} %`;
      return String(v);
    });

  const countLabel = !meta ? null
    : meta.total_unavailable ? t("seo:results.countUnknown")
      : meta.total_capped
        ? t("seo:results.countCapped", { count: (2000).toLocaleString("de-AT") })
        : t("seo:results.count", { count: (total ?? 0).toLocaleString("de-AT") });

  const sortOptions = [
    { value: "recommended", label: t("seo:sort.recommended") },
    { value: "newest", label: t("seo:sort.newest") },
    { value: "price_asc", label: t("seo:sort.priceAsc") },
    { value: "price_desc", label: t("seo:sort.priceDesc") },
    { value: "discount", label: t("seo:sort.discount") },
  ];

  return (
    <div className="min-h-dvh bg-zinc-950 text-zinc-100" data-testid="seo-landing-page">
      <main className="mx-auto max-w-7xl space-y-7 px-3 py-6 sm:px-6 sm:py-10">
        <header className="max-w-3xl space-y-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-400/80"
            data-testid="seo-eyebrow">
            {t("seo:filters.preset")}
          </p>
          <h1 className="text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl"
            data-testid="seo-h1">
            {loc.h1}
          </h1>
          <p className="text-sm text-zinc-400 sm:text-base" data-testid="seo-intro">
            {loc.intro}
          </p>
          {loc.synonyms?.length > 0 && (
            <p className="text-xs text-zinc-600" data-testid="seo-synonyms">
              {t("seo:synonyms.label")}: {loc.synonyms.join(" · ")}
            </p>
          )}
        </header>

        <section className="space-y-3 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
          {ambiguous && (
            <div className="flex flex-wrap items-center gap-2" data-testid="seo-tt-switch">
              <span className="text-xs text-zinc-500">{t("seo:switch.label")}</span>
              {[["", "any"], ["buy", "buy"], ["rent", "rent"]].map(([val, key]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`seo-tt-${key}`}
                  aria-pressed={tt === val}
                  onClick={() => setTt(val)}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    tt === val
                      ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-300"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {t(`seo:switch.${key}`)}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {chips.map((c) => (
              <span key={c} className="rounded-full bg-zinc-800/80 px-2.5 py-1 text-[11px] text-zinc-300">
                {c}
              </span>
            ))}
            <button
              type="button"
              data-testid="seo-deals-toggle"
              aria-pressed={dealsOnly}
              onClick={() => setDealsOnly((v) => !v)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                dealsOnly
                  ? "border-emerald-500/70 bg-emerald-500/15 text-emerald-300"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
              }`}
            >
              {dealsOnly ? t("seo:filters.dealsOnly") : t("seo:filters.allOffers")}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-44">
                <DarkSelect compact testid="seo-sort" value={sort}
                  options={sortOptions} onChange={setSort} />
              </div>
              <button
                type="button"
                data-testid="seo-edit-filters"
                onClick={openFullSearch}
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:border-zinc-500"
              >
                <SlidersHorizontal size={13} aria-hidden /> {t("seo:filters.editAll")}
              </button>
            </div>
          </div>
          {countLabel && (
            <p className="text-xs text-zinc-500" data-testid="seo-result-count">{countLabel}</p>
          )}
          <SeoAlertButton page={page} lang={lang} path={seoPath(lang, page)} />
        </section>

        {thin && items.length > 0 && (
          <p className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-200/90"
            data-testid="seo-thin-notice">
            {t("seo:results.thinNotice")}
          </p>
        )}

        {feedQ.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-zinc-500">
            <Loader2 size={16} className="animate-spin" aria-hidden /> {t("seo:results.loading")}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-8 text-center"
            data-testid="seo-results-empty">
            <p className="text-sm font-semibold text-zinc-200">{t("seo:results.emptyTitle")}</p>
            <p className="mx-auto mt-2 max-w-xl text-xs text-zinc-500">{t("seo:results.emptyBody")}</p>
            <button
              type="button"
              data-testid="seo-empty-open-search"
              onClick={openFullSearch}
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl border border-zinc-700 px-4 py-2 text-xs text-zinc-300 hover:border-emerald-500/60"
            >
              <SlidersHorizontal size={12} aria-hidden /> {t("seo:filters.openSearch")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="seo-results-grid">
            {items.map((card, i) => (
              <PublicDealCard key={`${card.id}-${i}`} card={card} index={i} onOpen={openCard} />
            ))}
          </div>
        )}

        {feedQ.hasNextPage && (
          <div className="flex justify-center">
            <button
              type="button"
              data-testid="seo-load-more"
              onClick={() => feedQ.fetchNextPage()}
              disabled={feedQ.isFetchingNextPage}
              className="rounded-xl border border-zinc-700 px-5 py-2.5 text-sm font-medium text-zinc-300 hover:border-zinc-500 disabled:opacity-50"
            >
              {feedQ.isFetchingNextPage ? t("seo:results.loading") : t("seo:results.loadMore")}
            </button>
          </div>
        )}

        <SeoLinkList
          title={t("seo:related.title")}
          links={relatedSeoPages(page, lang)}
          testid="seo-related-searches"
        />
      </main>
    </div>
  );
}
